import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../axiosInterceptor';
import type { Recording } from '../../Component/Sessions/types';

type RecordingsState = {
  loading: boolean;
  recordings: Recording[];
  totalCount: number;
  currentPage: number;
  perPage: number;
  error: boolean;
  errorMessage: string;
  isGeneratingSummary: boolean;
};

const initialState: RecordingsState = {
  loading: false,
  recordings: [],
  totalCount: 0,
  currentPage: 1,
  perPage: 10,
  error: false,
  errorMessage: '',
  isGeneratingSummary: false,
};

export const getRecordings = createAsyncThunk<{
  recordings: Recording[];
  total_count: number;
  current_page: number;
  per_page: number;
}, { page: number; perPage: number }>(
  'getRecordings',
  async ({ page, perPage }: { page: number; perPage: number }) => {
    const { data } = await api.get(`/api/sessions/recordings?Page=${page}&per_page=${perPage}`);
    console.log('📦 [GET RECORDINGS] Raw response from backend:', data);
    return data;
  }
);

export const generateSummary = createAsyncThunk<{
  sessionId: string;
  summary: string;
}, string>(
  'generateSummary',
  async (sessionId: string) => {
    console.log('🚀 [GENERATE SUMMARY] Triggering POST for session:', sessionId);
    const { data } = await api.post(`/api/sessions/${sessionId}/generate-summary`);
    console.log('📦 [GENERATE SUMMARY] Full API Response:', data);
    
    // Support multiple response formats:
    // 1. data is string
    // 2. { summary: "..." }
    // 3. { response: { summary: "..." } }
    // 4. { data: { summary: "..." } }
    const summary = data?.summary || 
                    data?.response?.summary || 
                    data?.data?.summary || 
                    (typeof data === 'string' ? data : JSON.stringify(data));
                    
    console.log('📜 [GENERATE SUMMARY] Extracted Summary:', summary.substring(0, 50) + '...');
    return { sessionId, summary };
  }
);

const getRecordingsSlice = createSlice({
  name: 'getRecordings',
  initialState,
  reducers: {
    resetRecordings: (state) => {
      state.loading = false;
      state.recordings = [];
      state.totalCount = 0;
      state.currentPage = 1;
      state.perPage = 10;
      state.error = false;
      state.errorMessage = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getRecordings.pending, (state) => {
      state.loading = true;
      state.error = false;
      state.errorMessage = '';
    });
    builder.addCase(getRecordings.fulfilled, (state, action) => {
      state.loading = false;
      state.recordings = action.payload.recordings;
      state.totalCount = action.payload.total_count;
      state.currentPage = action.payload.current_page;
      state.perPage = action.payload.per_page;
    });
    builder.addCase(getRecordings.rejected, (state, action) => {
      state.loading = false;
      state.error = true;
      state.errorMessage = action.error.message ?? 'Failed to load recordings.';
    });
    builder.addCase(generateSummary.pending, (state) => {
      state.isGeneratingSummary = true;
      state.error = false;
    });
    builder.addCase(generateSummary.fulfilled, (state, action) => {
      state.isGeneratingSummary = false;
      const recording = state.recordings.find(r => r.id === action.payload.sessionId);
      if (recording) {
        recording.summary = action.payload.summary;
      }
    });
    builder.addCase(generateSummary.rejected, (state, action) => {
      state.isGeneratingSummary = false;
      state.error = true;
      state.errorMessage = action.error.message ?? 'Failed to generate summary.';
    });
  },
});

export const { resetRecordings } = getRecordingsSlice.actions;
export default getRecordingsSlice.reducer;
